import React from "react";
import {
    Alert,
    Box,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import CclDrawer from "../../../../../../../components/common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../../../../../../components/common/cclDrawer/cclDrawerActionBar";
import CclButton from "../../../../../../../components/common/cclButtons/cclButton";
import CclLoadingButton from "../../../../../../../components/common/cclButtons/cclLoadingButton";
import {
    useRequestScoringMutation,
    useLazyGetReportOptionsQuery,
    useVerifyFacilitatorMutation,
} from "../../../../../../../services/cclTokenedAssessmentApi";
import {
    BatchScoringRequest,
    ReportOptionGroup,
    SelectedReportOption,
    VerifyFacilitatorResult,
} from "../../../../../../../services/types/assessmentAPITypes";
import { UserClaimsService } from "../../../../../../../services/currentUserService/currentUserService";

export type RequestScoringDrawerProps = {
    open: boolean;
    instrumentKey: number;
    esiKeys: number[];
    sessionKey: number;
    onClose: () => void;
    onSuccess: (esiKeys: number[]) => void;
    onFailure: () => void;
    source?: "session" | "registration" | "my-access";
};

const RequestScoringDrawer: React.FC<RequestScoringDrawerProps> = (props) => {
    const [englishCopy, setEnglishCopy] = React.useState<boolean>(true);
    const [selectedOptions, setSelectedOptions] = React.useState<SelectedReportOption[]>([]);
    const [facilitatorEmail, setFacilitatorEmail] = React.useState<string>("");
    const [useMeAsFacilitator, setUseMeAsFacilitator] = React.useState<boolean>(false);
    const [facilitatorValidated, setFacilitatorValidated] = React.useState<boolean | null>(null);
    const [verifiedFacilitator, setVerifiedFacilitator] =
        React.useState<VerifyFacilitatorResult | null>(null);

    const [requestScoring, { isLoading }] = useRequestScoringMutation();
    const [verifyFacilitator, { isLoading: verifying }] = useVerifyFacilitatorMutation();
    const [
        getReportOptions,
        { data: reportOptionsResponse, isLoading: optionsLoading, isError: optionsError },
    ] = useLazyGetReportOptionsQuery();

    const claimsService = new UserClaimsService();
    const currentUserEmail = claimsService.GetCurrentUserEmail();

    React.useEffect(() => {
        if (props.open && props.instrumentKey > 0 && props.esiKeys.length > 0) {
            getReportOptions({
                instrumentId: props.instrumentKey,
                participantId: props.esiKeys[0],
            });
        }
    }, [props.open, props.instrumentKey, props.esiKeys, getReportOptions]);

    React.useEffect(() => {
        if (reportOptionsResponse?.reportOptions) {
            setSelectedOptions(
                reportOptionsResponse.reportOptions.map((group) => ({
                    scoreReportOptionTypeId: group.scoreReportOptionTypeId,
                    value: group.defaultValue,
                })),
            );
        }
    }, [reportOptionsResponse]);

    // Reset facilitator state when drawer opens/closes
    React.useEffect(() => {
        if (!props.open) {
            setFacilitatorEmail("");
            setUseMeAsFacilitator(false);
            setFacilitatorValidated(null);
            setVerifiedFacilitator(null);
        }
    }, [props.open]);

    const runVerification = (email: string) => {
        verifyFacilitator({ email, instrumentKey: props.instrumentKey })
            .unwrap()
            .then((result) => {
                setVerifiedFacilitator(result);
                setFacilitatorValidated(true);
            })
            .catch(() => {
                setVerifiedFacilitator(null);
                setFacilitatorValidated(false);
            });
    };

    const handleUseMeChange = (checked: boolean) => {
        setUseMeAsFacilitator(checked);
        if (checked) {
            setFacilitatorEmail(currentUserEmail);
            runVerification(currentUserEmail);
        } else {
            setFacilitatorEmail("");
            setFacilitatorValidated(null);
            setVerifiedFacilitator(null);
        }
    };

    const handleFacilitatorEmailChange = (value: string) => {
        setFacilitatorEmail(value);
        setFacilitatorValidated(null);
        setVerifiedFacilitator(null);
    };

    const handleValidateFacilitator = () => {
        runVerification(facilitatorEmail);
    };

    const handleOptionChange = (scoreReportOptionTypeId: number, value: string) => {
        setSelectedOptions((prev) =>
            prev.map((opt) =>
                opt.scoreReportOptionTypeId === scoreReportOptionTypeId ? { ...opt, value } : opt,
            ),
        );
    };

    const callRequestScoring = () => {
        const request: BatchScoringRequest = {
            batchRequest: {
                esKey: props.sessionKey,
                instrumentId: props.instrumentKey,
                englishCopy: englishCopy,
                esiKeys: props.esiKeys,
                selectedOptions: selectedOptions.length > 0 ? selectedOptions : undefined,
                facilitatorImKey: verifiedFacilitator?.imKey ?? -1,
            },
        };

        requestScoring(request)
            .unwrap()
            .then(() => {
                props.onSuccess(props.esiKeys);
                props.onClose();
            })
            .catch(() => {
                props.onFailure();
            });
    };

    const facilitatorInvalid = facilitatorValidated === false;
    const facilitatorNotValidated = facilitatorValidated !== true;
    const submitDisabled =
        props.esiKeys.length === 0 ||
        isLoading ||
        optionsLoading ||
        optionsError ||
        facilitatorInvalid ||
        facilitatorNotValidated ||
        (reportOptionsResponse?.reportOptions ?? []).some(
            (g) =>
                g.required &&
                !(
                    selectedOptions.find(
                        (o) => o.scoreReportOptionTypeId === g.scoreReportOptionTypeId,
                    )?.value ?? g.defaultValue
                ),
        );

    return (
        <>
            <CclDrawer
                open={props.open}
                onClose={props.onClose}
                title="Request Scoring"
                width="standard"
            >
                <Box component="form" noValidate autoComplete="off">
                    <Stack direction="column" spacing={4}>
                        <Alert severity="info">
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                <li>
                                    Scoring requires a completed self-assessment and at least 4
                                    rater submissions
                                </li>
                                <li>
                                    1 Boss, 3 Peer, and 3 Direct Report rater submissions are
                                    recommended for a quality feedback report
                                </li>
                                <li>View rater completion status in the table to the left</li>
                                <li>Assessments cannot be rescored here once scored</li>
                            </ul>
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                            {props.esiKeys.length} participant
                            {props.esiKeys.length !== 1 ? "s" : ""} selected
                        </Typography>
                        <Divider />
                        {optionsLoading ? (
                            <CircularProgress size={20} />
                        ) : optionsError ? (
                            <Typography variant="body2" color="error">
                                Failed to load report options.
                            </Typography>
                        ) : reportOptionsResponse?.reportOptions &&
                          reportOptionsResponse.reportOptions.length > 0 ? (
                            reportOptionsResponse.reportOptions.map((group: ReportOptionGroup) => {
                                const selectedValue =
                                    selectedOptions.find(
                                        (o) =>
                                            o.scoreReportOptionTypeId ===
                                            group.scoreReportOptionTypeId,
                                    )?.value ?? group.defaultValue;

                                const isMissing = group.required && !selectedValue;
                                return (
                                    <FormControl
                                        key={group.scoreReportOptionTypeId}
                                        error={isMissing}
                                    >
                                        <InputLabel
                                            id={`option-label-${group.scoreReportOptionTypeId}`}
                                        >
                                            {group.display}
                                            {group.required ? " *" : ""}
                                        </InputLabel>
                                        <Select
                                            size="small"
                                            labelId={`option-label-${group.scoreReportOptionTypeId}`}
                                            label={`${group.display}${group.required ? " *" : ""}`}
                                            value={selectedValue}
                                            onChange={(e) =>
                                                handleOptionChange(
                                                    group.scoreReportOptionTypeId,
                                                    e.target.value as string,
                                                )
                                            }
                                        >
                                            {group.reportOptionItems.map((item) => (
                                                <MenuItem
                                                    key={item.reportOptionId}
                                                    value={item.reportOptionValue}
                                                >
                                                    {item.reportOptionDescription}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {isMissing && (
                                            <FormHelperText>This field is required.</FormHelperText>
                                        )}
                                    </FormControl>
                                );
                            })
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No report options available for this instrument.
                            </Typography>
                        )}
                        <Divider />
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                                label="Facilitator Email"
                                value={facilitatorEmail}
                                onChange={(e) => handleFacilitatorEmailChange(e.target.value)}
                                disabled={useMeAsFacilitator}
                                size="small"
                                fullWidth
                            />
                            <CclLoadingButton
                                mode="primary"
                                onClick={handleValidateFacilitator}
                                disabled={
                                    !facilitatorEmail.trim() || verifying || useMeAsFacilitator
                                }
                                loading={verifying}
                                sx={{
                                    whiteSpace: "nowrap",
                                    fontSize: "12px",
                                    paddingLeft: "9px",
                                    paddingRight: "9px",
                                    paddingTop: "4px",
                                    paddingBottom: "4px",
                                }}
                            >
                                Validate
                            </CclLoadingButton>
                        </Stack>
                        <Stack direction="column" spacing={1}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={useMeAsFacilitator}
                                        onChange={(e) => handleUseMeChange(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label="Use Me As Facilitator"
                            />
                            {facilitatorInvalid && (
                                <Alert severity="warning">
                                    The requested facilitator is not authorized for this instrument
                                    type. Please enter a valid facilitator email.
                                </Alert>
                            )}
                        </Stack>
                        <Divider sx={{ mt: 2 }} />
                        <FormControlLabel
                            sx={{ mt: 1 }}
                            control={
                                <Checkbox
                                    checked={englishCopy}
                                    onChange={(e) => setEnglishCopy(e.target.checked)}
                                    size="small"
                                />
                            }
                            label="English Copy"
                        />
                    </Stack>
                    <CclDrawerActionBar>
                        <CclLoadingButton
                            onClick={callRequestScoring}
                            mode={"primary"}
                            disabled={submitDisabled}
                            loading={isLoading}
                        >
                            Submit
                        </CclLoadingButton>
                        <CclButton onClick={props.onClose} mode={"secondary"}>
                            Cancel
                        </CclButton>
                    </CclDrawerActionBar>
                </Box>
            </CclDrawer>
        </>
    );
};

export default RequestScoringDrawer;
